import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@/services/api-client';
import type { ArchiveCalendarResponse } from '@/services/api-client';

interface UseArchiveCalendarParams {
  gameId: string | null;
  month: string | null;
  subscriptionTier?: string | null;
  enabled?: boolean;
}

interface UseArchiveCalendarReturn {
  data: ArchiveCalendarResponse | undefined;
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
}

/**
 * React Query hook to fetch archive calendar data for a specific game and month
 *
 * Features:
 * - Automatic caching with 5-minute stale time (matches backend cache)
 * - Request deduplication (multiple components = 1 API call)
 * - Automatic retry with exponential backoff (3 retries)
 * - Background refetching when window regains focus
 *
 * @param gameId - The ID of the game (e.g., "mlb")
 * @param month - Month string in YYYY-MM format (e.g., "2025-11")
 * @param enabled - Optional flag to enable/disable the query (default: true if gameId and month provided)
 * @returns Calendar data, loading state, error state, and refetch function
 *
 * @example
 * ```tsx
 * const { data, isLoading, error } = useArchiveCalendar({
 *   gameId: 'mlb',
 *   month: '2025-11'
 * });
 * ```
 */
export function useArchiveCalendar({
  gameId,
  month,
  subscriptionTier = null,
  enabled = true,
}: UseArchiveCalendarParams): UseArchiveCalendarReturn {
  const {
    data,
    isLoading,
    error,
    refetch,
  } = useQuery({
    // Include subscription tier in cache key to prevent stale data when tier changes
    // Free and premium users see different challenge statuses, so they need separate caches
    queryKey: ['archiveCalendar', gameId, month, subscriptionTier],
    queryFn: async () => {
      if (!gameId || !month) {
        throw new Error('Game ID and month are required');
      }
      const guestToken = localStorage.getItem('hub_guest_token') ?? undefined;
      return apiClient.getArchiveCalendar(gameId, month, guestToken);
    },
    enabled: enabled && !!gameId && !!month,
    // staleTime and retry are configured globally in QueryClientProvider
    // staleTime: 5 minutes (matches backend cache)
    // retry: 3 times with exponential backoff
  });

  return {
    data,
    isLoading,
    error: error as Error | null,
    refetch: () => { refetch(); },
  };
}
